import React, { JSX, FC, useState, useMemo, useRef, useEffect, MutableRefObject } from "react";
import { NavigateFunction, useNavigate, useParams } from "react-router";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { handleError } from "utils/handleError";

import { useDispatch, useSelector } from "react-redux";
import { AppStateType } from "redux/reducers/mainReducer";
import { AppDispatch } from "redux/store/store";
import {
  createModification,
  deleteModification,
  dublicateModification,
} from "redux/thunks/products.thunk";

import { Button, List, notification, Skeleton } from "antd";
import ModificationCard from "components/Products/ModificationCard";
import PrimaryButton from "components/ui/PrimaryButton/PrimaryButton";
import ConfirmModal from "components/ui/modals/ConfirmModal/ConfirmModal";

import "./style.css";

import { ModificationCreateType, ModificationType } from "products/types";
import { IFormFields } from "pages/ImportMasterPage";

import { APP_PATHES } from "constants/appPathes";

interface IProductModificationsWrapperProps {
  isLoading: boolean;
}

const ProductModificationsWrapper: FC<IProductModificationsWrapperProps> = (): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate: NavigateFunction = useNavigate();
  const { dataId, modId } = useParams<{ mode?: string, dataId?: string, modId?: string }>();

  const form = useFormInstance<IFormFields>();

  const containerRef: MutableRefObject<any> = useRef(null);

  const {
    selected: selectedModification,
    isFetching: isLoadingModifications,
  } = useSelector((state: AppStateType) => state.productsSlice.modifications);
  const {
    selected: selectedProduct,
  } = useSelector((state: AppStateType) => state.productsSlice.products);

  const [isShowDeleteModal, setIsShowDeleteModal] = useState<boolean>(false);

  useEffect(() => {
    // скролл до выделенной модификации, если сейчас она вне зоны видимости контейнера
    if (selectedModification?.id && containerRef.current) {
      const activeElement: HTMLElement | null = containerRef.current.querySelector(
        `[id="${selectedModification?.id}"]`
      );

      if (activeElement) {
        const containerRect: DOMRect = containerRef.current.getBoundingClientRect();
        const elementRect: DOMRect = activeElement.getBoundingClientRect();

        // Проверяем, находится ли элемент в зоне видимости контейнера
        const isElementVisible: boolean =
          elementRect.top >= containerRect.top &&
          elementRect.bottom <= containerRect.bottom;

        // Если элемент вне зоны видимости, выполняем скролл
        if (!isElementVisible) {
          activeElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest", // Прокручивает только до ближайшей границы
          });
        }
      }
    }
  }, [selectedModification?.id, selectedProduct?.modifications]);

  const isAddButtonDisabled: boolean = !selectedProduct?.modifications
    || selectedProduct?.modifications?.length === 0
    || selectedProduct?.modifications?.length >= 30
    || !dataId;

  const getBaseModification = (): ModificationType => {
    return selectedProduct?.modifications?.find(({ id }) => id === Number(modId));
  };

  const deleteText: string = useMemo(() => {
    const { is_base } = getBaseModification() || {};

    return is_base
      ? "Продукт будет удален."
      : "Модификация будет удалена.";
  }, [getBaseModification()]);

  // Генерирует название модификации при добавлении нового варианта с уникальным порядковым номером
  const generateNewName = (modifications: ModificationType[]): string => {
    const allNewVariants: ModificationType[] = modifications
      .filter(({ name }) => typeof name === "string" && name.startsWith("Новый вариант_"));

    const indices: number[] = allNewVariants.map(({ name }) => {
      const match: RegExpMatchArray = name.match(/^Новый вариант_(\d+)$/); // Регулярное выражение для извлечения числа

      return match ? parseInt(match[1], 10) : 0; // Если число найдено, преобразуем в число, иначе 0
    });

    const maxIndex: number = indices.length > 0 ? Math.max(...indices) : 0;
    const newIdx: number = maxIndex + 1;

    return `Новый вариант_${newIdx}`;
  };

  const onAddHandler = (): void => {
    if (!dataId) return;

    const { warehouse } = form.getFieldsValue();

    dispatch(createModification({
      name: generateNewName(selectedProduct?.modifications),
      product_id: Number(dataId),
      show_in_catalog: false,
      warehouse_id: warehouse
    }))
      .then((response: ModificationCreateType) => {
        // Очистка полей перед заполнением информации о созданной модификации
        form.resetFields([
          "name", "code", "minCount", "description", "price", "properties", "photo", "showInCatalog", "isBase",
        ]);

        navigate(`${APP_PATHES.IMPORT_MASTER_MANUAL}/${dataId}/${response.id}`);
      })
      .catch((error) => handleError("Ошибка при добавлении модификации", error));

  };

  const onDuplicationHandler = (): void => {
    dispatch(dublicateModification(Number(modId)))
      .then((response: ModificationType) => navigate(
        `${APP_PATHES.IMPORT_MASTER_MANUAL}/${dataId}/${response.id}`
      ))
      .catch((error: any) => handleError("Ошибка при дублировании модификации", error));
  };

  const onDeleteHandler = async (): Promise<void> => {
    const { is_base } = selectedProduct?.modifications
      ?.find(({ id }) => id === Number(modId)) || {};

    dispatch(deleteModification({ modification_id: selectedModification.id }))
      .then(() => {
        const navigateTo: string = is_base
          ? APP_PATHES.IMPORT
          : `${APP_PATHES.IMPORT_MASTER_MANUAL}/${dataId}/${selectedProduct.modifications?.at(0)?.id}`;

        setIsShowDeleteModal(false);
        navigate(navigateTo);
      })
      .catch((error: any) => {
        handleError("Ошибка при удалении модификации", error);
      });
  };

  // Базовая модификация всегда сверху
  const sortedModifications: ModificationType[] = [...selectedProduct?.modifications ?? []].sort((a, b) => {
    if (a.is_base === b.is_base) return 0;

    return a.is_base ? -1 : 1;
  });

  return (
    <div className="modifications_wrapper h-full w-full">
      <div
        ref={containerRef}
        className="options_block"
      >
        <List
          dataSource={sortedModifications || []}
          renderItem={(modification: ModificationType, idx: number) => (
            <Skeleton
              avatar={{ className: "skeleton-list-item" }}
              paragraph={{ rows: 1 }}
              className="px-4 py-3 cursor-pointer mb-3"
              style={{ border: "1px solid #1e4bd2", borderRadius: "12px" }}
              loading={isLoadingModifications}
              active={isLoadingModifications}
            >
              <ModificationCard
                key={modification?.id}
                modification={modification}
                isFirst={idx === 0}
              />
            </Skeleton>
          )}>
        </List>
      </div>

      <div className="actions_wrapper flex flex-col gap-2">
        <div className="flex justify-between gap-2">
          <PrimaryButton
            className="w-full"
            text="Добавить вариант"
            onClickHandler={onAddHandler}
            isDisabled={isAddButtonDisabled}
          />
        </div>
        <div className="flex gap-2">
          <Button
            size="large"
            className="w-full grow-1/2"
            onClick={onDuplicationHandler}
            disabled={!selectedModification || !modId || isAddButtonDisabled}
          >
            Дублировать
          </Button>
          <Button
            size="large"
            className="w-full grow-1/2"
            onClick={() => setIsShowDeleteModal(true)}
            disabled={!selectedModification || !modId}>
            Удалить выбранный
          </Button>
        </div>
      </div>
      <ConfirmModal
        text={deleteText}
        isOpen={!!isShowDeleteModal}
        onOkHandler={onDeleteHandler}
        onCancelHandler={() => setIsShowDeleteModal(false)}
      />
    </div>
  );
};

export default ProductModificationsWrapper;
